<script>
import { whatsappUsernameOf } from 'dashboard/helper/whatsappUsername';
import { applyDialingPrefix } from 'dashboard/helper/phoneHelper';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { useAdmin } from 'dashboard/composables/useAdmin';
import ContactInfoRow from './ContactInfoRow.vue';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import SocialIcons from './SocialIcons.vue';
import EditContact from './EditContact.vue';
import ContactMergeModal from 'dashboard/modules/contact/ContactMergeModal.vue';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

import {
  isAConversationRoute,
  isAInboxViewRoute,
  getConversationDashboardRoute,
} from '../../../../helper/routeHelpers';
import { emitter } from 'shared/helpers/mitt';

export default {
  components: {
    NextButton,
    ContactInfoRow,
    EditContact,
    Thumbnail,
    SocialIcons,
    ContactMergeModal,
  },
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    showAvatar: {
      type: Boolean,
      default: true,
    },
    conversationId: {
      type: [Number, String],
      default: null,
    },
  },
  emits: ['panelClose'],
  setup() {
    const { isAdmin } = useAdmin();
    return {
      isAdmin,
    };
  },
  data() {
    return {
      showEditModal: false,
      showMergeModal: false,
      showDeleteModal: false,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'contacts/getUIFlags',
      phoneConfigured: 'phone/configured',
      phoneStatus: 'phone/getStatus',
      currentAccount: 'accounts/getCurrentAccount',
      getConversationById: 'getConversationById',
      getInboxById: 'inboxes/getInboxById',
    }),
    currentConversation() {
      return this.conversationId
        ? this.getConversationById(this.conversationId)
        : null;
    },
    currentInbox() {
      return this.currentConversation?.inbox_id
        ? this.getInboxById(this.currentConversation.inbox_id)
        : null;
    },
    contactProfileLink() {
      return `/app/accounts/${this.$route.params.accountId}/contacts/${this.contact.id}`;
    },
    additionalAttributes() {
      return this.contact.additional_attributes || {};
    },
    location() {
      const {
        country = '',
        city = '',
        country_code: countryCode,
      } = this.additionalAttributes;
      const cityAndCountry = [city, country].filter(item => !!item).join(', ');

      if (!cityAndCountry) {
        return '';
      }
      return this.findCountryFlag(countryCode, cityAndCountry);
    },
    ltv() {
      return this.additionalAttributes.ltv;
    },
    socialProfiles() {
      const {
        social_profiles: socialProfiles,
        screen_name: twitterScreenName,
        social_telegram_user_name: telegramUsername,
      } = this.additionalAttributes;
      return {
        twitter: twitterScreenName,
        telegram: telegramUsername,
        ...(socialProfiles || {}),
      };
    },
    formattedWhatsappUsername() {
      const username = whatsappUsernameOf(this.additionalAttributes);
      return username ? `@${username}` : '';
    },
    // Delete Modal
    confirmDeleteMessage() {
      return ` ${this.contact.name}?`;
    },
    isPhoneWidgetAvailable() {
      return this.phoneConfigured && this.phoneStatus === 'connected';
    },
    contactPhoneNumber() {
      if (!this.contact.phone_number) return '';
      const prefix = this.currentInbox?.phoneDialingPrefix;
      if (prefix) {
        return this.contact.phone_number.startsWith('+')
          ? '00' + this.contact.phone_number.substring(1)
          : this.contact.phone_number;
      }
      return this.contact.phone_number;
    },
    formattedPhoneNumber() {
      if (!this.contact.phone_number) return '';

      return applyDialingPrefix(
        this.contact.phone_number,
        this.currentInbox?.phoneDialingPrefix
      );
    },
  },
  watch: {
    'contact.id': {
      handler(id) {
        this.$store.dispatch('contacts/fetchContactableInbox', id);
      },
      immediate: true,
    },
  },
  mounted() {
    this.$store.dispatch('inboxes/get');
    this.$store.dispatch('userPhoneCredentials/get');
  },
  methods: {
    dynamicTime,
    toggleEditModal() {
      this.showEditModal = !this.showEditModal;
    },
    openNewConversation() {
      emitter.emit(BUS_EVENTS.OPEN_NEW_CONVERSATION, {
        contactId: String(this.contact.id),
      });
    },
    toggleDeleteModal() {
      this.showDeleteModal = !this.showDeleteModal;
    },
    confirmDeletion() {
      this.deleteContact(this.contact);
      this.closeDelete();
    },
    closeDelete() {
      this.showDeleteModal = false;
      this.showEditModal = false;
    },
    findCountryFlag(countryCode, cityAndCountry) {
      try {
        if (!countryCode) {
          return `${cityAndCountry} 🌎`;
        }

        const code = countryCode?.toLowerCase();
        return `${cityAndCountry} <span class="fi fi-${code} size-3.5"></span>`;
      } catch (error) {
        return '';
      }
    },
    async deleteContact({ id }) {
      try {
        await this.$store.dispatch('contacts/delete', id);
        this.$emit('panelClose');
        useAlert(this.$t('DELETE_CONTACT.API.SUCCESS_MESSAGE'));

        if (isAConversationRoute(this.$route.name)) {
          this.$router.push({
            name: getConversationDashboardRoute(this.$route.name),
          });
        } else if (isAInboxViewRoute(this.$route.name)) {
          this.$router.push({
            name: 'inbox_view',
          });
        } else if (this.$route.name !== 'contacts_dashboard') {
          this.$router.push({
            name: 'contacts_dashboard',
          });
        }
      } catch (error) {
        useAlert(
          error.message
            ? error.message
            : this.$t('DELETE_CONTACT.API.ERROR_MESSAGE')
        );
      }
    },
    closeMergeModal() {
      this.showMergeModal = false;
    },
    openMergeModal() {
      this.showMergeModal = true;
    },
    async onCopy(e) {
      e.preventDefault();
      await copyTextToClipboard(this.contact.name);
      useAlert(this.$t('CONTACT_PANEL.COPY_SUCCESSFUL'));
    },
    handlePhoneClick() {
      if (this.currentAccount.dialing_mode === 'direct') {
        this.$store.dispatch('phone/makeCall', {
          number: this.formattedPhoneNumber,
        });
      } else {
        emitter.emit(BUS_EVENTS.SHOW_PHONE_DIALER, this.formattedPhoneNumber);
      }
    },
  },
};
</script>

<template>
  <div class="relative items-center w-full p-4">
    <div class="flex flex-col w-full gap-2 text-left rtl:text-right">
      <div class="flex flex-row justify-between">
        <Thumbnail
          v-if="showAvatar"
          :src="contact.thumbnail"
          size="48px"
          :username="contact.name"
          :status="contact.availability_status"
        />
      </div>

      <div class="flex flex-col items-start gap-1.5 min-w-0 w-full">
        <div v-if="showAvatar" class="flex items-center w-full min-w-0 gap-3">
          <h3
            class="flex-shrink max-w-full min-w-0 my-0 text-base break-words text-n-slate-12"
            translate="no"
          >
            {{ contact.name }}
          </h3>
          <div class="flex flex-row items-center gap-2">
            <span
              v-if="contact.created_at"
              v-tooltip="
                `${$t('CONTACT_PANEL.CREATED_AT_LABEL')} ${dynamicTime(
                  contact.created_at
                )}`
              "
              class="i-lucide-info text-sm text-n-slate-10"
            />
            <a
              v-tooltip="$t('CONTACT_PANEL.VIEW_PROFILE')"
              :href="contactProfileLink"
              target="_blank"
              rel="noopener nofollow noreferrer"
              class="leading-3"
            >
              <span class="i-lucide-external-link text-sm text-n-slate-10" />
            </a>
            <NextButton
              v-tooltip="$t('CONTACT_PANEL.COPY_NAME')"
              ghost
              xs
              slate
              class="ltr:-ml-1 rtl:-mr-1"
              icon="i-lucide-clipboard"
              @click="onCopy"
            />
          </div>
        </div>

        <p
          v-if="additionalAttributes.description"
          class="break-words mb-0.5 whitespace-pre-line"
        >
          {{ additionalAttributes.description }}
        </p>
        <div class="flex flex-col items-start w-full gap-2">
          <ContactInfoRow
            v-tooltip="$t('CONTACT_PANEL.EMAIL_ADDRESS')"
            :href="contact.email ? `mailto:${contact.email}` : ''"
            :value="contact.email"
            icon="mail"
            emoji="✉️"
            show-copy
          />
          <ContactInfoRow
            v-if="isPhoneWidgetAvailable"
            v-tooltip="$t('CONTACT_PANEL.PHONE_NUMBER')"
            href="#"
            :value="contactPhoneNumber"
            :value-prefix="currentInbox?.phoneDialingPrefix"
            icon="call"
            emoji="📞"
            show-copy
            @click="handlePhoneClick"
          />
          <ContactInfoRow
            v-else
            v-tooltip="$t('CONTACT_PANEL.PHONE_NUMBER')"
            :href="formattedPhoneNumber ? `tel:${formattedPhoneNumber}` : ''"
            :value="contactPhoneNumber"
            :value-prefix="currentInbox?.phoneDialingPrefix"
            icon="call"
            emoji="📞"
            click-through
            show-copy
          />
          <ContactInfoRow
            v-if="formattedWhatsappUsername"
            v-tooltip="$t('CONTACT_PANEL.WHATSAPP_USERNAME')"
            :value="formattedWhatsappUsername"
            icon="brand-whatsapp"
            emoji="💬"
            show-copy
          />
          <ContactInfoRow
            v-if="contact.identifier"
            v-tooltip="$t('CONTACT_PANEL.IDENTIFIER')"
            :value="contact.identifier"
            icon="contact-identify"
            emoji="🪪"
          />
          <ContactInfoRow
            v-tooltip="$t('CONTACT_PANEL.COMPANY')"
            :value="additionalAttributes.company_name"
            icon="building-bank"
            emoji="🏢"
          />
          <ContactInfoRow
            v-if="location || additionalAttributes.location"
            v-tooltip="$t('CONTACT_PANEL.LOCATION')"
            :value="location || additionalAttributes.location"
            icon="map"
            emoji="🌍"
          />
          <ContactInfoRow
            v-if="ltv"
            v-tooltip="$t('CONTACT_PANEL.LTV')"
            :value="ltv"
            icon="money-calculator"
            emoji="💰"
          />
          <SocialIcons :social-profiles="socialProfiles" />
        </div>
      </div>
      <div class="flex items-center w-full mt-0.5 gap-2">
        <NextButton
          v-tooltip.top-end="$t('CONTACT_PANEL.NEW_MESSAGE')"
          icon="i-ph-chat-circle-dots"
          slate
          faded
          sm
          @click="openNewConversation"
        />
        <NextButton
          v-tooltip.top-end="$t('EDIT_CONTACT.BUTTON_LABEL')"
          icon="i-ph-pencil-simple"
          slate
          faded
          sm
          @click="toggleEditModal"
        />
        <NextButton
          v-tooltip.top-end="$t('CONTACT_PANEL.MERGE_CONTACT')"
          icon="i-ph-arrows-merge"
          slate
          faded
          sm
          :disabled="uiFlags.isMerging"
          @click="openMergeModal"
        />
        <NextButton
          v-if="isAdmin"
          v-tooltip.top-end="$t('DELETE_CONTACT.BUTTON_LABEL')"
          icon="i-ph-trash"
          slate
          faded
          sm
          ruby
          :disabled="uiFlags.isDeleting"
          @click="toggleDeleteModal"
        />
      </div>
      <EditContact
        v-if="showEditModal"
        :show="showEditModal"
        :contact="contact"
        @cancel="toggleEditModal"
      />
      <ContactMergeModal
        v-if="showMergeModal"
        :primary-contact="contact"
        :show="showMergeModal"
        @close="closeMergeModal"
      />
    </div>
    <woot-delete-modal
      v-if="showDeleteModal"
      v-model:show="showDeleteModal"
      :on-close="closeDelete"
      :on-confirm="confirmDeletion"
      :title="$t('DELETE_CONTACT.CONFIRM.TITLE')"
      :message="$t('DELETE_CONTACT.CONFIRM.MESSAGE')"
      :message-value="confirmDeleteMessage"
      :confirm-text="$t('DELETE_CONTACT.CONFIRM.YES')"
      :reject-text="$t('DELETE_CONTACT.CONFIRM.NO')"
    />
  </div>
</template>
